import { useMemo } from 'react';
import { QueryConfig, useQuery } from 'react-query';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';

import type {
    ActualContentBlockClientData,
    CourseContainerWidgetAdditionalInfo,
    MainPageClientData,
} from '@yandex-int/k-common';
import { getMainPage, GetMainPageRequest } from '@yandex-int/k-common/client/api/defs/get-main-page';
import qs from 'query-string';

import { getExperimentTestIds } from '../components/experiments/selectors';
import { getUatraits } from '../components/uatraits/selectors';
import { useMobileAppWebview } from '../utils/useMobileAppWebview';

export type MainPageData = {
    page?: MainPageClientData;
};

export interface GetMainPageRequestWithDefaults extends Omit<GetMainPageRequest, 'date' | 'grade'> {
    date?: GetMainPageRequest['date'];
    grade?: GetMainPageRequest['grade'];
}

export function useMainPageQuery(
    queryParams: GetMainPageRequestWithDefaults,
    queryConfig: QueryConfig<MainPageData> = {}
) {
    const defaultDate = useDefaultDate();
    const experimentIds = useSelector(getExperimentTestIds);
    const { grade, is_public_blocks: isPublicBlocks } = queryParams;

    const isMobileApp = useMobileAppWebview();
    const uatraits = useSelector(getUatraits);

    let platform: GetMainPageRequest['platform'];
    if (isMobileApp) {
        platform = 'app';
    } else if (uatraits.isMobile) {
        platform = 'mobile_site';
    } else {
        platform = 'desktop_site';
    }

    return useQuery<MainPageData>(
        ['mainPage', grade, isPublicBlocks, platform],
        () =>
            getMainPage({
                date: defaultDate,
                experiments: experimentIds,
                platform,
                ...queryParams,
            } as GetMainPageRequest),
        { staleTime: Infinity, enabled: !!grade, ...queryConfig }
    );
}

export function useCourseContainersQuery(queryParams: GetMainPageRequest, queryConfig?: QueryConfig<MainPageData>) {
    const { grade } = queryParams;
    const initialQuery = useMainPageQuery(queryParams, { staleTime: Infinity, ...queryConfig });

    const filteredData = useMemo(() => {
        if (!initialQuery.data) {
            return null;
        }

        const cc = initialQuery.data.page?.blocks?.find((block) => block.type === 'course_container');

        if (!cc || !('additional_info' in cc)) {
            return null;
        }

        const info = cc?.additional_info as CourseContainerWidgetAdditionalInfo;

        return info.course_container_group.find((courseContainer) => courseContainer.grade === Number(grade));
    }, [grade, initialQuery.data]);

    return { ...initialQuery, data: filteredData };
}

export function useActualContentQuery(queryParams: GetMainPageRequest, queryConfig?: QueryConfig<MainPageData>) {
    const initialQuery = useMainPageQuery(queryParams, { staleTime: Infinity, ...queryConfig });

    const filteredData = useMemo(() => {
        if (!initialQuery.data) {
            return null;
        }

        return initialQuery.data.page?.blocks?.find((block) => block.type === 'actual_content') ?? null;
    }, [initialQuery.data]);

    return { ...initialQuery, data: filteredData as ActualContentBlockClientData | null };
}

const useDefaultDate = () => {
    const { search } = useLocation();
    const params = qs.parse(search);

    return String(params.now || new Date().toISOString().split('T')[0]);
};

export function useMainPageCourseContainersQuery(queryParams: GetMainPageRequestWithDefaults, queryConfig = {}) {
    const { grade } = queryParams;
    const initialQuery = useMainPageQuery(queryParams, queryConfig);

    const filteredData = useMemo(() => {
        if (!initialQuery.data) {
            return null;
        }

        const cc = initialQuery.data.page?.blocks?.find((block) => block.type === 'course_container');

        if (!cc || !('additional_info' in cc)) {
            return null;
        }

        const info = cc?.additional_info as CourseContainerWidgetAdditionalInfo;

        return info.course_container_group.find((courseContainer) => courseContainer.grade === Number(grade));
    }, [grade, initialQuery.data]);

    return { ...initialQuery, data: filteredData };
}
